/**
 * settingsEstrutura.js — ONDE cada parâmetro aparece na tela Configurações
 * CI Sistemas de Informática Ltda. — AcessoiD (Onda 100)
 *
 * A tela antiga era organizada pela ORDEM EM QUE AS COISAS FORAM CRIADAS: uma aba
 * única empilhava oito blocos e 62 campos abertos. A consolidação por passagem — o
 * coração do registro — ficava no fim da página, depois da Impressão, com o nome
 * "Filtro Anti-Leitura Duplicada"; a Janela de Abandono morava em "Financeiro"; os
 * limiares de confiança numa aba e as faixas de brilho que escolhem qual deles vale,
 * em outra.
 *
 * Aqui a organização é pela FINALIDADE, em quatro grupos de um menu lateral, e a
 * tela mostra uma seção por vez. Nenhuma chave mudou de nome, de rota ou de valor —
 * só de lugar. Um item é o nome de uma chave de `SETTINGS_META`, ou `{ bloco }`
 * para os poucos trechos que têm desenho próprio (os cartões do modo, os sliders
 * de limiar, o seletor CPU/GPU, a Impressão).
 *
 * `blocoDasChaves` diz quais chaves cada bloco edita: é o que permite ao teste
 * provar que TODA chave da tela antiga está na nova, exatamente uma vez.
 */

const modo = (s) => s.plate_filter_mode || 'none';

export const blocoDasChaves = {
  modoDeFiltragem: ['plate_filter_mode'],
  limiares: ['dynamic_threshold_enabled', 'confidence_threshold_day', 'confidence_threshold_dusk', 'confidence_threshold_night'],
  superexposicao: ['overexp_enabled', 'overexp_threshold', 'overexp_gamma'],
  // Estes não editam chave de `settings` por esta tela: têm rota própria ou só mostram.
  impressao: [],
  processador: [],
  linhaDoTempo: [],
  semaforoBroker: [], // Onda 141: a senha do broker e o estado das controladoras têm rota própria
  referenciaDePayloads: [],
  estadoDoVigia: [],
  telegram: [],   // Onda 117: estado do bot, procurar grupos, mensagem de teste, últimos envios
};

export const ESTRUTURA = [
  {
    grupo: 'Operação',
    secoes: [
      {
        id: 'operacao', titulo: 'Operação',
        desc: 'O perfil da plataforma e o comportamento durante a transição com o sistema legado.',
        // Onda 133c: os quatro juntos e em todas as verticais, como antes da 133 (que escondeu os
        // modos de ticket do clube e do condomínio por uma suposição que só valia para o Ipê).
        grupos: [
          { itens: ['client_name', 'system_vertical', 'parallel_mode', 'single_camera_mode'] },
          // Onda 135: a tolerância de permanência morava aqui, em "Relatórios de acesso". Onda 178: foi para
          // Integração › Liberação da saída, ao lado do liga/desliga — ela decide o evento de saída e a
          // liberação, não só os relatórios.
          // Onda 154: clube e condomínio (inclusive o corporativo) podem cobrar ticket pela plataforma.
          // No estacionamento a cobrança é a própria vertical, e a chave não aparece.
          { titulo: 'Cobrança de ticket', verticais: ['clube_social', 'condominio'], soSuperadmin: true, itens: ['cobranca_habilitada'] },
        ],
      },
      {
        id: 'cobranca', titulo: 'Permanência e cobrança', verticais: ['estacionamento'], cobranca: true,
        desc: 'Quanto tempo o cliente tem para sair depois de pagar, e quando um ticket sem saída é dado como abandonado.',
        grupos: [
          { itens: ['exit_tolerance_min', 'max_parking_duration'] },
          { titulo: 'Reconciliação e ticket aberto à mão', itens: ['reconciliation_grace_hours', 'manual_ticket_dedupe_minutes'] },
        ],
      },
      {
        id: 'impressao', titulo: 'Impressão do ticket', verticais: ['estacionamento'], cobranca: true,
        desc: 'Onde o ticket é impresso, o papel, e o texto do cabeçalho e do rodapé.',
        grupos: [{ itens: [{ bloco: 'impressao' }] }],
      },
      {
        id: 'evidencias', titulo: 'Evidências e armazenamento',
        desc: 'O que é gravado a cada leitura, com que qualidade, e por quanto tempo fica guardado.',
        grupos: [
          { titulo: 'Por quanto tempo guardar', itens: ['snapshot_retention_days', 'crop_retention_days'] },
          { titulo: 'O que gravar', itens: ['snapshot_quality', 'save_plate_crops'] },
          { titulo: 'Fotos de leituras descartadas pela zona', itens: ['save_discarded_evidence', 'discarded_min_conf', 'discarded_near_zone', 'discarded_throttle_s'] },
        ],
      },
    ],
  },
  {
    grupo: 'Reconhecimento',
    secoes: [
      {
        id: 'captura', titulo: 'Captura e motor',
        desc: 'Com que frequência as câmeras são lidas, quanto se espera pelo motor, e em que processador ele roda.',
        grupos: [
          { titulo: 'Captura', itens: ['frame_interval'] },
          { titulo: 'Tempos-limite', nota: 'Os dois andam juntos: o do motor tem de ser MENOR que o da plataforma, para o motor desistir antes e a fila não acumular quadros velhos.', itens: ['fastalpr_timeout', 'inference_timeout'] },
          { titulo: 'Processador', itens: [{ bloco: 'processador' }] },
          { titulo: 'Burst — em manutenção', recolhido: true, itens: ['burst_frames', 'burst_interval_ms'] },
        ],
      },
      {
        id: 'passagem', titulo: 'Registro de passagem',
        desc: 'Como as várias leituras de um mesmo veículo viram um único registro — e o que impede o mesmo carro de ser registrado duas vezes.',
        grupos: [
          { itens: [{ bloco: 'modoDeFiltragem' }] },   // o bloco já traz o próprio rótulo
          { titulo: 'Quando registrar', visivelSe: (s) => modo(s) === 'passage', itens: ['passage_gap_ms', 'passage_commit_readings', 'passage_strong_min_char', 'passage_strong_min_width', 'passage_settle_width'] },
          { titulo: 'Correções automáticas', visivelSe: (s) => modo(s) === 'passage', itens: ['passage_follow_outside_roi', 'passage_correct_after_commit'] },
          {
            titulo: 'Marcar para revisão', visivelSe: (s) => modo(s) === 'passage',
            nota: 'São duas regras, e nenhuma muda o registro: só acrescentam o selo "aguardando revisão" na Qualidade do OCR. A primeira pega a placa pequena e bem de lado registrada com UMA leitura; a segunda, só na câmera de entrada, pega a placa pequena e de viés com qualquer número de leituras.',
            itens: ['passage_review_max_prop', 'passage_review_max_width', 'passage_review_small_width', 'passage_review_small_prop'],
          },
          { titulo: 'Veículos parados', visivelSe: (s) => modo(s) === 'passage', itens: ['static_plate_seconds', 'static_plate_forget_seconds', 'static_plate_after_commit'] },
          { titulo: 'Conferência com o cadastro', visivelSe: (s) => modo(s) === 'passage', itens: ['plate_match_known', 'plate_match_min_char'] },
          { titulo: 'Sentido da saída', visivelSe: (s) => modo(s) === 'passage', itens: ['exit_direction_mode', 'exit_direction_lateral_max', 'exit_direction_still_max', 'exit_direction_per_camera'] },
          { titulo: 'Parâmetros do modo Similaridade', visivelSe: (s) => modo(s) === 'similarity', itens: ['similarity_window_seconds'] },
          { titulo: 'Parâmetros do modo Dupla Leitura', visivelSe: (s) => modo(s) === 'double_read', itens: ['double_read_window_seconds', 'double_read_min_reads'] },
          { titulo: 'Intervalo entre registros da mesma placa', itens: ['dedup_window_entry', 'dedup_window_exit'] },
          { titulo: 'Leituras que nem entram na conta', itens: ['min_plate_length', 'min_bbox_width'] },
          { titulo: 'Avançado — aproximação e limites por câmera', recolhido: true, visivelSe: (s) => modo(s) === 'passage', itens: ['passage_growth_tolerance', 'passage_per_camera'] },
        ],
      },
      {
        // Onda 127: a zona de observação fecha TICKET (rtspService) — só existe no estacionamento.
        id: 'observacao', titulo: 'Zona de observação', verticais: ['estacionamento'],
        desc: 'A segunda zona da câmera de entrada, que reconhece o veículo que saiu sem a câmera de saída ler.',
        grupos: [{ itens: ['observation_zone_enabled', 'observation_min_minutes_after_entry', 'observation_min_sightings', 'observation_window_seconds', 'observation_auto_close'] }],
      },
      {
        id: 'confianca', titulo: 'Confiança e luminosidade',
        desc: 'A confiança mínima para aceitar uma leitura, que muda com a luz — e como o motor decide em que faixa de luz está.',
        grupos: [
          { titulo: 'Confiança mínima por faixa de luz', itens: [{ bloco: 'limiares' }] },
          { titulo: 'Como o motor escolhe a faixa', itens: ['threshold_mode', 'luminance_night_max', 'luminance_dusk_max', 'luminance_ema_alpha', 'luminance_hysteresis', 'luminance_overrides'] },
          { titulo: 'Relógio (reserva) — só vale sem brilho medido, ou no modo "clock"', recolhido: true, itens: [{ bloco: 'linhaDoTempo' }, 'night_start_hour', 'night_end_hour', 'dusk_morning_start_hour', 'dusk_morning_end_hour', 'dusk_evening_start_hour', 'dusk_evening_end_hour'] },
        ],
      },
      {
        id: 'ocr', titulo: 'OCR e pré-processamento',
        desc: 'O que o motor faz com a imagem antes de ler a placa, e quando descarta uma leitura insegura.',
        grupos: [
          { titulo: 'Leitura', itens: ['min_char_confidence', 'plate_crop_margin', 'ocr_source'] },
          { titulo: 'Redução de ruído', itens: ['denoise_mode', 'denoise_search_window'] },
          { titulo: 'Classificador de layout (Mercosul × antiga)', itens: ['layout_classifier_enabled', 'layout_min_conf', 'layout_ocr_max_conf'] },
          { titulo: 'Correção de superexposição — em manutenção', recolhido: true, itens: [{ bloco: 'superexposicao' }] },
        ],
      },
    ],
  },
  {
    grupo: 'Integração',
    secoes: [
      {
        // Onda 119: o receptor do sistema de gestão pode ficar fora do ar um fim de semana.
        id: 'entrega', titulo: 'Entrega dos eventos ao sistema de gestão', verticais: ['clube_social', 'condominio'],
        desc: 'Por quanto tempo a plataforma insiste quando o receptor do sistema de gestão está fora do ar, e o que ela faz quando ele volta.',
        grupos: [{
          nota: 'O endereço do receptor, o segredo e as tentativas ficam na tela da integração. Aqui fica só o que evita perder evento: em 26 e 27/09/2026 o receptor do Ipê ficou desligado e a plataforma desistiu de 406 eventos antes de ele voltar.',
          itens: ['integration_retry_window_hours', 'integration_rescue_days'],
        }, {
          // Onda 181: o evento tem prazo — o ticket é impresso ao receber, e uma entrada entregue horas depois
          // imprime de novo (Ipê, 19/10/2026).
          titulo: 'Validade do evento',
          nota: 'Passado o prazo sem entrega, o evento expira e não é mais tentado; fica na Supervisão com o motivo. A correção de um evento expirado não é enviada. 0 = sem validade.',
          itens: ['integration_entry_event_ttl_min', 'integration_exit_event_ttl_min'],
        }],
      },
      {
        // Onda 133b: vale em TODAS as verticais. A 133 tirou esta seção do clube porque o Ipê não
        // usa cancela — critério errado (Iatagã, 04/10/2026): a vertical é de qualquer clube, não de
        // um cliente. Só se esconde o que a vertical não pode ter por construção (ticket, cobrança).
        id: 'cancela', titulo: 'Cancela e webhooks',
        desc: 'O endereço que recebe os comandos de abertura de cancela, e o formato de cada mensagem enviada.',
        grupos: [
          { itens: ['webhook_url'] },
          { titulo: 'Referência completa de payloads', itens: [{ bloco: 'referenciaDePayloads' }] },
        ],
      },
      {
        // Onda 166: característica da instalação cujo cliente cobra o estacionamento num sistema próprio
        // (o Ipê Clube); vale no clube e no condomínio. No estacionamento a cobrança é da plataforma.
        id: 'liberacao', titulo: 'Liberação da saída pelo pagamento', verticais: ['clube_social', 'condominio'],
        desc: 'Quando o sistema de gestão do cliente cobra o estacionamento: ele registra cada pagamento pela API e a plataforma decide, a cada placa lida na saída, se a cancela ou o portão abre.',
        grupos: [
          // Onda 178: a saída livre na tolerância — o liga/desliga e os minutos (do estacionamento, que tem a
          // própria tolerância em "Permanência e cobrança", a seção inteira não aparece).
          { titulo: 'Saída livre na tolerância', itens: ['access_tolerance_enabled', 'access_tolerance_min'] },
          { nota: 'Ligue a liberação primeiro e confira as decisões no evento de saída (bloco gate) com as saídas reais; a abertura automática vem depois, com a controladora de saída instalada.', itens: ['exit_payment_release_enabled', 'exit_auto_open_enabled', 'exit_authorization_window_min'] },
        ],
      },
      {
        // Onda 141: o Semáforo da Guarita é do clube e do condomínio (decisão do Iatagã, 12/10/2026).
        // O estacionamento não passa pelo sinal; a vertical, não o cliente, é que não o tem.
        id: 'semaforo', titulo: 'Semáforo da guarita', verticais: ['clube_social', 'condominio'],
        desc: 'As lâmpadas da bancada da guarita: verde para associado ou morador, vermelho (com bipe) para quem não é. Liga-se por câmera, na tela Câmeras; aqui ficam o broker e o sinal.',
        grupos: [
          { titulo: 'Broker e controladoras', nota: 'O broker Mosquitto roda no próprio servidor desta instalação e cada controladora (Keyestudio na bancada) conecta a ele pela rede local. A senha da plataforma é gravada aqui e nunca é mostrada.', itens: [{ bloco: 'semaforoBroker' }, 'semaforo_mqtt_url', 'semaforo_mqtt_username', 'semaforo_topic_prefix'] },
          { titulo: 'O sinal', itens: ['semaforo_pulse_ms', 'semaforo_buzzer'] },
        ],
      },
    ],
  },
  {
    // Onda 102 (fase 3): o que o servidor lê e nenhuma tela mostrava.
    grupo: 'Sistema',
    secoes: [
      {
        id: 'vigias', titulo: 'Vigias e alertas',
        desc: 'Os alarmes que avisam quando algo para de funcionar sem que nenhum serviço caia: a entrada que deixa de abrir tickets, a câmera que fica muda.',
        grupos: [
          {
            titulo: 'Vigia do fluxo de entrada', verticais: ['estacionamento'], cobranca: true,
            nota: 'O alarme vermelho exige os dois lados ao mesmo tempo: saídas sem ticket em alta E a entrada sem abrir tickets agora. Só as saídas sem ticket não alarmam — depois de uma pane corrigida elas continuam por horas, e um alarme que segue aceso com o problema resolvido ensina a ignorá-lo. A mudança leva até um minuto para aparecer no painel de saúde.',
            itens: [{ bloco: 'estadoDoVigia' }, 'entry_watch_enabled', 'entry_watch_window_min', 'entry_watch_min_exits', 'entry_watch_orphan_ratio', 'entry_watch_confirmed_ratio', 'entry_watch_ratio', 'entry_watch_min_reference'],
          },
          { titulo: 'Câmeras', itens: ['camera_heartbeat_timeout_seconds'] },
          {
            titulo: 'Avisos pelo Telegram',
            nota: 'Os mesmos alarmes desta seção, mandados pelo Telegram a cada pessoa escolhida — para quem não está com a plataforma aberta. O aviso sai uma vez quando a falha começa, repete no intervalo de lembrete e avisa quando volta. O bot é configurado no servidor; aqui se escolhe quem recebe e quando.',
            itens: [{ bloco: 'telegram' }, 'alert_telegram_enabled', 'alert_telegram_chat_id', 'alert_site_name', 'alert_wait_minutes', 'alert_reminder_hours', 'alert_hours', 'alert_integration_minutes'],
          },
        ],
      },
      {
        id: 'app', titulo: 'App da guarita', verticais: ['estacionamento'], cobranca: true,
        desc: 'O aviso de versão nova que aparece nos aparelhos da guarita. O app avisa; quem instala é o operador.',
        grupos: [{
          nota: 'A ordem certa: primeiro publique o instalador novo no servidor, depois informe aqui a versão dele. Invertendo, todos os aparelhos avisam de uma versão que ainda não dá para baixar.',
          itens: ['app_version', 'app_min_version', 'app_update_notes', 'app_apk_url'],
        }],
      },
    ],
  },
];

export const todasAsSecoes = () => ESTRUTURA.flatMap(g => g.secoes.map(s => ({ ...s, grupo: g.grupo })));

/** Toda chave que a tela edita, na ordem em que aparece — com repetição, se houver. */
export function chavesDaTela() {
  return todasAsSecoes().flatMap(s => s.grupos.flatMap(g => g.itens.flatMap(
    item => (typeof item === 'string' ? [item] : blocoDasChaves[item.bloco] || [])
  )));
}

/** A seção vale para esta vertical? Sem vertical conhecida, mostra tudo. */
/**
 * A seção (ou o grupo) aparece nesta instalação? Pela vertical, como sempre; e, desde a Onda 154,
 * também quando é de cobrança (`cobranca: true`) e a instalação cobra ticket (`extra.cobranca`).
 * `soSuperadmin` esconde o trecho de quem não é superadministrador.
 */
export const valeNaVertical = (secao, vertical, extra = {}) => {
  if (secao.soSuperadmin && !extra.superadmin) return false;
  if (!secao.verticais || !vertical || secao.verticais.includes(vertical)) return true;
  return secao.cobranca === true && extra.cobranca === true;
};
